import { useState, useMemo, useEffect } from "react";
import { State as StateList } from "country-state-city";
import { useTheme } from "@mui/material";
import { GeographicRegion, State } from "../../../api/calendarEvent";

export interface StateDropdownProps {
  country: GeographicRegion | null;
  initialState?: State | null;
  onSelect: (state: State | null) => void;
}

const useStateDropdown = ({
  country,
  initialState,
  onSelect,
}: StateDropdownProps) => {
  const { spacing } = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [selectedState, setSelectedState] = useState<State | null>(null);

  const handleOptionSelect = (_event: unknown, newValue: State | null) => {
    setSelectedState(newValue);
    onSelect(newValue);
  };

  const allStates: State[] = useMemo(() => {
    if (!country) return [];

    try {
      const states = StateList.getStatesOfCountry(country.code);
      return states.map((state) => ({
        iso: state.isoCode,
        abbrev: "",
        name: state.name,
      }));
    } catch (error) {
      console.error("Error fetching states:", error);
      return [];
    }
  }, [country]);

  const filteredStates = useMemo(() => {
    if (!searchValue) return allStates;
    const searchLower = searchValue.toLowerCase();
    return allStates.filter((state) =>
      state.name.toLowerCase().includes(searchLower),
    );
  }, [searchValue, allStates]);

  const handleInputChange = (_event: unknown, newValue: string) => {
    setIsLoading(true);
    setSearchValue(newValue);
    setIsLoading(false);
  };

  useEffect(() => {
    setSelectedState(null);
    onSelect(null);
  }, [country, onSelect]);

  useEffect(() => {
    if (initialState && filteredStates.length > 0) {
      const states = filteredStates.find(
        (opt) => opt.iso === initialState?.iso,
      );
      setSelectedState(states || null);
    }
  }, [initialState, filteredStates]);

  return {
    options: filteredStates,
    handleInputChange,
    isLoading,
    spacing,
    selectedState,
    handleOptionSelect,
    hasStates: allStates.length > 0,
  };
};

export default useStateDropdown;
